import type { TenantCompanyProfile } from '@forq/platform-data';
import { effectiveTaxCode, lineKindLabels, taxCodeDefinition, type Invoice, type InvoiceLineKind } from '@forq/finance-data';

/**
 * Ausgangsrechnung als druckfertiges HTML im FTW-Layout, gerendert aus dem `Invoice` -- gedruckt
 * bzw. als PDF über den Browser (Button im erzeugten HTML). DATEN-getrieben, kein hinterlegtes PDF;
 * derselbe Briefkopf/Fußzeile aus dem Firmenprofil wie die übrigen FORQ-Belege.
 *
 * Die Pflichtangaben nach § 14 UStG stehen alle im Beleg: vollständige Anschrift von Verkäufer
 * (Briefkopf + Fußzeile) und Käufer, USt-IdNr. bzw. Steuernummer (Fußzeile), Ausstellungsdatum,
 * fortlaufende Rechnungsnummer, Menge/Art der Leistung, Leistungsdatum und das nach Steuersätzen
 * aufgeschlüsselte Entgelt. Die verbindlichen Summen kommen unverändert aus dem `Invoice`
 * (in der DB gerechnet); die Aufschlüsselung nach Sätzen wird nur zur Anzeige gebildet.
 *
 * Zweisprachig (de/en): die Belegsprache reist mit dem `Invoice` (kommt beim Abrechnen aus dem
 * Vorgang, sonst Standard de). Ist sie 'en', wird der ganze Beleg englisch gerendert; die frei
 * editierbaren deutschen Profiltexte (Anrede/Zahlungssatz/Fußzeile) gelten dann NICHT -- statt-
 * dessen greifen die eingebauten englischen Vorgaben. Beträge/Datum bleiben im dt. Format.
 */

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
function money(value: number): string { return euro.format(value); }
function qty(value: number): string { return qtyFormat.format(value); }
function round2(value: number): number { return Math.round(value * 100) / 100; }
function dateLabel(value: string | null | undefined): string {
  const [year, month, day] = (value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

// --- Sprache: feste Beschriftungen + eingebaute Standardtexte je Belegsprache ---------------------
type DocLang = 'de' | 'en';

/** Belegsprache aus der Rechnung ableiten (nur en schaltet um; alles andere = Deutsch als Standard). */
function langOf(invoice: Invoice): DocLang {
  return invoice.language === 'en' ? 'en' : 'de';
}

interface InvStrings {
  readonly print: string;
  readonly headingInvoice: string; readonly headingCredit: string;
  readonly docNoInvoice: string; readonly docNoCredit: string; readonly caseNo: string;
  readonly dateInvoice: string; readonly dateCredit: string; readonly serviceDate: string; readonly custNo: string;
  readonly salutation: string; readonly itemsSection: string;
  readonly pos: string; readonly description: string; readonly qty: string; readonly unit: string; readonly unitPrice: string; readonly discount: string; readonly vat: string; readonly net: string;
  readonly servicePeriod: string;
  readonly netAmount: string; readonly vatOn: (rate: string, base: string) => string; readonly plusVatTotal: string; readonly total: string;
  readonly marginShare: string; readonly marginNote: string;
  readonly creditRef: (invoiceNumber: string) => string;
  readonly cashDiscount: (percent: string, days: number) => string;
  readonly creditIntroDefault: string; readonly creditPaymentDefault: string;
  readonly invoiceIntroDefault: string; readonly paymentTextDefault: string;
  readonly companyFallback: string; readonly contact: string; readonly bankDetails: string; readonly company: string;
  readonly phone: string; readonly fax: string; readonly director: string; readonly vatId: string; readonly taxNo: string;
}

const STRINGS: Readonly<Record<DocLang, InvStrings>> = {
  de: {
    print: 'Drucken / als PDF speichern',
    headingInvoice: 'RECHNUNG', headingCredit: 'GUTSCHRIFT',
    docNoInvoice: 'Rechnungs-Nr.:', docNoCredit: 'Gutschrift-Nr.:', caseNo: 'Vorgang:',
    dateInvoice: 'Rechnungsdatum:', dateCredit: 'Datum:', serviceDate: 'Leistungsdatum:', servicePeriod: 'Leistungszeitraum:', custNo: 'Kunden-Nr.:',
    salutation: 'Sehr geehrte Damen und Herren,', itemsSection: 'Positionen',
    pos: 'Pos.', description: 'Bezeichnung', qty: 'Menge', unit: 'Einheit', unitPrice: 'Einzelpreis', discount: 'Rabatt', vat: 'USt', net: 'Netto',
    netAmount: 'Nettobetrag', vatOn: (rate, base) => `Umsatzsteuer ${rate} % (auf ${base})`, plusVatTotal: 'zzgl. Umsatzsteuer gesamt', total: 'Gesamtbetrag',
    marginShare: 'davon differenzbesteuert (§ 25a UStG)',
    // Pflichtangabe nach § 14a Abs. 6 UStG. Der Wortlaut ist im Gesetz vorgegeben.
    marginNote: 'Gebrauchtgegenstände/Sonderregelung. Die Umsatzsteuer wird nach § 25a UStG nicht gesondert ausgewiesen; ein Vorsteuerabzug aus dieser Rechnung ist nicht möglich.',
    creditRef: (n) => `Diese Gutschrift bezieht sich auf Rechnung <strong>${n}</strong>.`,
    cashDiscount: (percent, days) => ` Bei Zahlung innerhalb von ${days} Tagen gewähren wir ${percent} % Skonto.`,
    creditIntroDefault: 'wir schreiben Ihnen die nachstehenden Positionen wie folgt gut.',
    creditPaymentDefault: 'Der Gutschriftbetrag wird Ihrem Konto gutgeschrieben bzw. mit offenen Posten verrechnet.',
    invoiceIntroDefault: 'vielen Dank für Ihren Auftrag. Für die nachstehenden Leistungen berechnen wir Ihnen:',
    paymentTextDefault: 'Zahlbar bis {fällig} ohne Abzug auf das unten genannte Konto. Bitte geben Sie bei der Zahlung die Rechnungsnummer {nummer} an.',
    companyFallback: 'Firma', contact: 'Kontakt', bankDetails: 'Bankverbindung', company: 'Unternehmen',
    phone: 'Tel.:', fax: 'Fax:', director: 'Geschäftsführer:', vatId: 'USt-IdNr.:', taxNo: 'Steuer-Nr.:',
  },
  en: {
    print: 'Print / save as PDF',
    headingInvoice: 'INVOICE', headingCredit: 'CREDIT NOTE',
    docNoInvoice: 'Invoice No.:', docNoCredit: 'Credit note No.:', caseNo: 'Reference:',
    dateInvoice: 'Invoice date:', dateCredit: 'Date:', serviceDate: 'Service date:', servicePeriod: 'Service period:', custNo: 'Customer No.:',
    salutation: 'Dear Sir or Madam,', itemsSection: 'Items',
    pos: 'Item', description: 'Description', qty: 'Qty', unit: 'Unit', unitPrice: 'Unit price', discount: 'Discount', vat: 'VAT', net: 'Net',
    netAmount: 'Net amount', vatOn: (rate, base) => `VAT ${rate} % (on ${base})`, plusVatTotal: 'plus VAT total', total: 'Total',
    marginShare: 'thereof margin scheme (sec. 25a German VAT Act)',
    marginNote: 'Margin scheme — second-hand goods. VAT is not shown separately under sec. 25a German VAT Act; no input VAT deduction is possible from this invoice.',
    creditRef: (n) => `This credit note refers to invoice <strong>${n}</strong>.`,
    cashDiscount: (percent, days) => ` For payment within ${days} days we grant ${percent} % cash discount.`,
    creditIntroDefault: 'we hereby credit you for the items listed below.',
    creditPaymentDefault: 'The credit amount will be credited to your account or offset against open items.',
    invoiceIntroDefault: 'thank you for your order. For the services listed below we charge you as follows:',
    paymentTextDefault: 'Payable by {fällig} without deduction to the account named below. Please quote invoice number {nummer} with your payment.',
    companyFallback: 'Company', contact: 'Contact', bankDetails: 'Bank details', company: 'Company',
    phone: 'Phone:', fax: 'Fax:', director: 'Managing Director:', vatId: 'VAT ID:', taxNo: 'Tax No.:',
  },
};

/** Positionsart-Beschriftung je Sprache (deutsch aus finance-data, englisch hier). */
const lineKindLabelsEn: Readonly<Record<InvoiceLineKind, string>> = {
  labour: 'Labour', material: 'Material', travel: 'Travel', service: 'Service',
  machine: 'Machine', freight: 'Freight', text: 'Text', other: 'Other',
};
function lineKindLabel(kind: InvoiceLineKind, lang: DocLang): string {
  return lang === 'en' ? lineKindLabelsEn[kind] : lineKindLabels[kind];
}

function letterhead(company: TenantCompanyProfile): string {
  const { identity, letterhead: lh } = company;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `<div class="hd"><div class="hd-slot hd-left">${slotLeft}</div><div class="hd-slot hd-center">${slotCenter}</div><div class="hd-slot hd-right">${slotRight}</div></div><hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile, L: InvStrings): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || L.companyFallback, [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col(L.contact, [contact.phone && `${L.phone} ${contact.phone}`, contact.fax && `${L.fax} ${contact.fax}`, contact.email, contact.website])}
    ${col(L.bankDetails, [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col(L.company, [identity.managingDirectors && `${L.director} ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId ? `${L.vatId} ${identity.vatId}` : identity.taxNumber && `${L.taxNo} ${identity.taxNumber}`])}
  </div>`;
}

/**
 * Leistungsdatum bzw. -zeitraum (§ 14 Abs. 4 Nr. 6 UStG) -- Pflichtangabe auf jeder Rechnung.
 *
 * BEFUND (09/2026): Hier stand `dateLabel(issued)`, also das RECHNUNGSDATUM unter der Beschriftung
 * „Leistungsdatum". Ein Leistungsdatum gab es im Datenmodell gar nicht. Jetzt kommt es aus dem
 * Beleg selbst; nur wenn ein Altbeleg (vor der Umstellung) keines trägt, wird ersatzweise das
 * Ausstellungsdatum gezeigt -- so, wie dieser Beleg schon immer gedruckt wurde.
 */
function serviceDateRow(invoice: Invoice, L: InvStrings, issued: string | null): string {
  const from = invoice.serviceDate ?? issued;
  const to = invoice.serviceEndDate;
  if (to && to !== from) {
    return `<tr><th>${esc(L.servicePeriod)}</th><td>${dateLabel(from)} – ${dateLabel(to)}</td></tr>`;
  }
  return `<tr><th>${esc(L.serviceDate)}</th><td>${dateLabel(from)}</td></tr>`;
}

/**
 * Traegt diese Zeile die Differenzbesteuerung (§ 25a UStG)?
 * Erkannt am Steuerschluessel, NICHT am Satz -- 0 % heisst fuer sich genommen nur „keine Steuer
 * ausgewiesen" und trifft ebenso auf Ausfuhr, § 13b und die innergemeinschaftliche Lieferung zu.
 */
function isMarginLine(line: Invoice['lines'][number]): boolean {
  const code = effectiveTaxCode(line);
  return code !== null && taxCodeDefinition(code).marginScheme;
}

function positionsTable(invoice: Invoice, L: InvStrings, lang: DocLang): string {
  // Die Rabattspalte erscheint nur, wenn tatsächlich rabattiert wurde -- sonst bliebe eine leere
  // Spalte stehen. § 14 Abs. 4 Nr. 7 UStG verlangt, dass eine Entgeltminderung erkennbar ist.
  const hasDiscount = invoice.lines.some((line) => (line.discountPercent ?? 0) > 0);
  const rows = invoice.lines.map((line, index) => `<tr>
      <td class="c">${index + 1}</td>
      <td><strong>${esc(line.description)}</strong>${line.kind !== 'text' && line.kind !== 'service' ? `<div class="postext">${esc(lineKindLabel(line.kind, lang))}</div>` : ''}</td>
      <td class="num">${line.kind === 'text' ? '' : qty(line.quantity)}</td>
      <td>${line.kind === 'text' ? '' : esc(line.unit)}</td>
      <td class="num">${line.kind === 'text' ? '' : money(line.unitPrice)}</td>
      ${hasDiscount ? `<td class="num">${line.kind === 'text' || !(line.discountPercent ?? 0) ? '' : `${qty(line.discountPercent)} %`}</td>` : ''}
      <td class="num">${line.kind === 'text' || isMarginLine(line) ? '' : `${qty(line.vatRate)} %`}</td>
      <td class="num">${line.kind === 'text' ? '' : money(line.netAmount)}</td>
    </tr>`).join('');
  return `<table class="pos">
    <thead><tr><th class="c">${esc(L.pos)}</th><th>${esc(L.description)}</th><th class="num">${esc(L.qty)}</th><th>${esc(L.unit)}</th><th class="num">${esc(L.unitPrice)}</th>${hasDiscount ? `<th class="num">${esc(L.discount)}</th>` : ''}<th class="num">${esc(L.vat)}</th><th class="num">${esc(L.net)}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

/** Entgelt nach Steuersätzen aufgeschlüsselt (Pflichtangabe) + die verbindlichen Summen aus dem Invoice. */
function totalsTable(invoice: Invoice, L: InvStrings): string {
  const byRate = new Map<number, { net: number; vat: number }>();
  let margenSumme = 0;
  for (const line of invoice.lines) {
    // Differenzbesteuerte Zeilen tauchen in der Steueraufteilung NICHT auf. § 14a Abs. 6 UStG
    // untersagt den gesonderten Steuerausweis -- eine Zeile „0 % auf 12.000 EUR" waere zwar
    // rechnerisch harmlos, wuerde aber eine Steuerbefreiung behaupten, die es nicht gibt.
    if (isMarginLine(line)) { margenSumme += line.netAmount; continue; }
    const bucket = byRate.get(line.vatRate) ?? { net: 0, vat: 0 };
    bucket.net += line.netAmount;
    bucket.vat += line.netAmount * (line.vatRate / 100);
    byRate.set(line.vatRate, bucket);
  }
  const rateRows = [...byRate.entries()]
    .sort(([a], [b]) => b - a)
    .map(([rate, sums]) => `<tr><th>${esc(L.vatOn(qty(rate), money(round2(sums.net))))}</th><td>${money(round2(sums.vat))}</td></tr>`)
    .join('');
  const row = (label: string, value: string, strong = false) =>
    `<tr class="${strong ? 'is-strong' : ''}"><th>${esc(label)}</th><td>${value}</td></tr>`;
  // Bei einem reinen Margenbeleg waere „Nettobetrag / zzgl. USt 0,00" irrefuehrend -- dort steht
  // nur der Gesamtbetrag plus der Pflichthinweis darunter.
  const nurMarge = margenSumme > 0 && byRate.size === 0;
  return `<table class="totals">
    ${nurMarge ? '' : row(L.netAmount, money(invoice.netTotal))}
    ${rateRows}
    ${nurMarge ? '' : row(L.plusVatTotal, money(invoice.vatTotal))}
    ${margenSumme > 0 && !nurMarge ? row(L.marginShare, money(round2(margenSumme))) : ''}
    ${row(L.total, money(invoice.grossTotal), true)}
  </table>`;
}

export interface InvoiceDocumentBuyer {
  readonly name: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  readonly vatId?: string;
}

export interface InvoiceDocumentContext {
  readonly invoice: Invoice;
  readonly company: TenantCompanyProfile;
  readonly buyer: InvoiceDocumentBuyer;
}

/** Vollständiges HTML einer Ausgangsrechnung/Gutschrift -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderInvoiceHtml(ctx: InvoiceDocumentContext): string {
  const { invoice, company, buyer } = ctx;
  const lang = langOf(invoice);
  const L = STRINGS[lang];
  const accent = company.letterhead.accentColor || '#e30613';
  const isCredit = invoice.kind === 'credit_note';
  const heading = isCredit ? L.headingCredit : L.headingInvoice;
  const documentNumber = invoice.invoiceNumber ?? invoice.proposalNumber;
  const issued = invoice.issueDate ?? invoice.createdAt.slice(0, 10);

  const buyerLines = [
    buyer.name,
    buyer.street,
    `${buyer.postalCode} ${buyer.city}`.trim(),
    buyer.countryCode && buyer.countryCode !== 'DE' ? buyer.countryCode : '',
  ].filter(Boolean).map((line) => esc(line)).join('<br>');

  // Vorlagen-Editor: Einleitung + Zahlungssatz kommen aus dem Firmenprofil (Platzhalter {kunde} {nummer}
  // {fällig}). Erst den frei editierbaren Text säubern, dann die sicheren Werte einsetzen -- Beleg läuft im iframe.
  const fill = (template: string): string => esc(template)
    .replace(/\{kunde\}/g, esc(buyer.name))
    .replace(/\{nummer\}/g, `<strong>${esc(documentNumber)}</strong>`)
    .replace(/\{fällig\}/g, `<strong>${dateLabel(invoice.dueDate)}</strong>`);

  // Die frei editierbaren deutschen Profiltexte gelten nur auf Deutsch; auf Englisch die eingebauten Vorgaben.
  const intro = isCredit
    ? fill(L.creditIntroDefault)
    : fill(lang === 'de' ? (company.documentDefaults.invoiceIntro || L.invoiceIntroDefault) : L.invoiceIntroDefault);

  // Pflichthinweis der Differenzbesteuerung -- ohne ihn ist die Rechnung formal unvollstaendig.
  const marginNote = invoice.lines.some(isMarginLine)
    ? `<p class="note">${esc(L.marginNote)}</p>`
    : '';
  const creditRef = isCredit && invoice.creditedInvoiceNumber
    ? `<p class="note">${L.creditRef(esc(invoice.creditedInvoiceNumber))}</p>`
    : '';

  const discount = company.documentDefaults.cashDiscountPercent > 0 && company.documentDefaults.cashDiscountDays > 0
    ? L.cashDiscount(qty(company.documentDefaults.cashDiscountPercent), company.documentDefaults.cashDiscountDays)
    : '';
  const payment = isCredit
    ? `<p class="note">${esc(L.creditPaymentDefault)}</p>`
    : `<p class="note">${fill(lang === 'de' ? (company.documentDefaults.invoicePaymentText || L.paymentTextDefault) : L.paymentTextDefault)}${esc(discount)}</p>`;

  // Eigener Fußzeilentext ist ein deutscher Profiltext -- auf Englisch nicht anzeigen.
  const footerText = lang === 'de' && company.documentDefaults.invoiceFooterText
    ? `<p class="note">${esc(company.documentDefaults.invoiceFooterText)}</p>`
    : '';

  return `<!doctype html>
<html lang="${esc(lang)}"><head><meta charset="utf-8"><title>${esc(heading)} ${esc(documentNumber)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .addr { min-width: 82mm; }
  .addr .sender { font-size: 8px; color: #666; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 4px; }
  .addr .to { font-size: 12px; line-height: 1.5; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 20px; font-weight: 800; margin-bottom: 6px; color: #111; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .intro { margin: 8mm 0 3mm; }
  h2.sec { font-size: 13px; font-weight: 700; margin: 6mm 0 2mm; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 11px; }
  table.pos th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.pos td.c, table.pos th.c { text-align: center; width: 10mm; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.pos .postext { margin-top: 3px; font-size: 10.5px; color: #666; }
  table.totals { margin: 4mm 0 0 auto; border-collapse: collapse; min-width: 92mm; font-size: 11.5px; }
  table.totals th { text-align: left; color: #444; font-weight: 500; padding: 3px 12px 3px 0; }
  table.totals td { text-align: right; font-weight: 700; padding: 3px 0; white-space: nowrap; }
  table.totals tr.is-strong th, table.totals tr.is-strong td { border-top: 1.5px solid var(--accent); font-size: 13px; color: #111; padding-top: 5px; }
  .note { font-size: 10.5px; color: #444; margin: 4mm 0 0; text-align: justify; white-space: pre-line; }
  .footer { margin-top: 12mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    table.pos thead { display: table-header-group; }
    table.pos tr, table.totals { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">${esc(L.print)}</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div class="addr">
        <div class="sender">${esc(company.identity.name || '')}${company.identity.address.street ? ` · ${esc(company.identity.address.street)} · ${esc(`${company.identity.address.postalCode} ${company.identity.address.city}`.trim())}` : ''}</div>
        <div class="to">${buyerLines}</div>
      </div>
      <div class="doc-head">
        <div class="doc-title">${esc(heading)}</div>
        <table class="meta">
          <tr><th>${esc(isCredit ? L.docNoCredit : L.docNoInvoice)}</th><td>${esc(documentNumber)}</td></tr>
          ${invoice.sourceNumber ? `<tr><th>${esc(L.caseNo)}</th><td>${esc(invoice.sourceNumber)}</td></tr>` : ''}
          <tr><th>${esc(isCredit ? L.dateCredit : L.dateInvoice)}</th><td>${dateLabel(issued)}</td></tr>
          ${serviceDateRow(invoice, L, issued)}
          ${invoice.customerNumber ? `<tr><th>${esc(L.custNo)}</th><td>${esc(invoice.customerNumber)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    <p class="intro">${esc(L.salutation)}<br>${intro}</p>
    ${creditRef}
    ${marginNote}
    <h2 class="sec">${esc(L.itemsSection)}</h2>
    ${positionsTable(invoice, L, lang)}
    ${totalsTable(invoice, L)}
    ${payment}
    ${footerText}
    ${footer(company, L)}
  </div>
</body></html>`;
}
